import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { API_URL } from '../lib/api';
import { LOGO_PATH, BUSINESS_NAME } from '../config/brand';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Scissors, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedService = searchParams.get('service');
  const { login } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const getRedirectPath = () => {
    if (selectedService) return `/client/book?service=${encodeURIComponent(selectedService)}`;
    return '/client/dashboard';
  };

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/signup', {
        email: form.email, password: form.password,
        name: form.name, phone: form.phone,
      });
      login(res.data.user, res.data.access_token);
      toast.success('Account created successfully!');
      navigate(getRedirectPath());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectPath = selectedService ? `/client/book?service=${encodeURIComponent(selectedService)}` : '/client/dashboard';
    window.location.href = `${API_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectPath)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 cursor-pointer mb-6" onClick={() => navigate('/')}>
            <img src={LOGO_PATH} alt={BUSINESS_NAME} className="h-10" />
          </div>
          <h1 className="text-2xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Create your account</h1>
          <p className="text-sm text-[#7A6B5E] mt-1">
            {selectedService ? <>Book <span className="font-semibold text-[#A96B49]">{selectedService}</span> for your pet</> : 'Start managing your pet\'s grooming needs'}
          </p>
        </div>

        <Card className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-medium)' }}>
          <CardContent className="p-8">
            <Button
              variant="outline"
              className="w-full rounded-xl border-[#E5E5E0] hover:bg-[#F5F5F0] mb-6 py-5"
              onClick={handleGoogleSignup}
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E5E5E0]" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#888]">or sign up with email</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-[#2D2D2D]">Full Name</Label>
                <Input value={form.name} onChange={handleChange('name')} placeholder="Your name" className="mt-1.5 rounded-lg bg-[#F5F5F0]/30 border-[#E5E5E0]" required data-testid="signup-name-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#2D2D2D]">Email</Label>
                <Input type="email" value={form.email} onChange={handleChange('email')} placeholder="you@example.com" className="mt-1.5 rounded-lg bg-[#F5F5F0]/30 border-[#E5E5E0]" required data-testid="signup-email-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#2D2D2D]">Phone</Label>
                <Input type="tel" value={form.phone} onChange={handleChange('phone')} placeholder="555-0000" className="mt-1.5 rounded-lg bg-[#F5F5F0]/30 border-[#E5E5E0]" data-testid="signup-phone-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#2D2D2D]">Password</Label>
                <div className="relative mt-1.5">
                  <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={handleChange('password')} placeholder="Min 6 characters" className="rounded-lg bg-[#F5F5F0]/30 border-[#E5E5E0] pr-10" required data-testid="signup-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888]">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#2D2D2D]">Confirm Password</Label>
                <Input type="password" value={form.confirm} onChange={handleChange('confirm')} placeholder="Confirm password" className="mt-1.5 rounded-lg bg-[#F5F5F0]/30 border-[#E5E5E0]" required data-testid="signup-confirm-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#2D2D2D] text-white hover:bg-[#1A1A1A] py-5 shadow-md hover:-translate-y-0.5 active:translate-y-0 mt-2" data-testid="signup-submit-btn">
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#5A5A5A] mt-6">
          Already have an account?{' '}
          <Link to={selectedService ? `/login?service=${encodeURIComponent(selectedService)}` : '/login'} className="text-[#2D2D2D] font-semibold hover:underline" data-testid="login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
